import { Directive, effect, ElementRef, inject, input, OnDestroy, output } from '@angular/core';
import { SecurityManagerService } from '@iupics-manager/managers/security-manager/security-manager.service';

@Directive({
  selector: '[iuNumberAnimation]',
  standalone: true,
})
export class NumberAnimationDirective implements OnDestroy {
  private readonly el = inject(ElementRef<HTMLElement>);
  private readonly securityManager = inject(SecurityManagerService);
  private animationFrameId: number | null = null;

  duration = input(1500);
  data = input<number | string | null | undefined>();
  playAnimation = input<boolean>(false);
  outputText = input(true);
  formatter = input<Intl.NumberFormat | ((value: number) => string) | undefined>(undefined);

  readonly numberEmitter = output<number>();

  constructor() {
    effect(() => {
      const shouldAnimate = this.playAnimation();
      const value = this.data();

      if (shouldAnimate) {
        this.startAnimation(value);
      } else {
        this.setFinalValue(value);
      }
    });
  }

  ngOnDestroy(): void {
    this.cancelCurrentAnimation();
  }

  private startAnimation(value: number | string | null | undefined): void {
    const targetNumber = this.parseNumber(value);

    if (targetNumber === 0) {
      this.updateDisplay(0);
      return;
    }

    this.animateToValue(targetNumber);
  }

  private setFinalValue(value: number | string | null | undefined): void {
    const number = this.parseNumber(value);
    this.updateDisplay(number);
  }

  private animateToValue(targetValue: number): void {
    this.cancelCurrentAnimation();

    let startTimestamp: number | null = null;
    const startValue = 0;

    const animate = (timestamp: number): void => {
      if (startTimestamp === null) {
        startTimestamp = timestamp;
      }

      const elapsed = timestamp - startTimestamp;
      const progress = Math.min(elapsed / this.duration(), 1);

      const easedProgress = this.easeOutCubic(progress);
      const currentValue = Math.floor(startValue + (targetValue - startValue) * easedProgress);

      this.updateDisplay(currentValue);

      if (progress < 1) {
        this.animationFrameId = requestAnimationFrame(animate);
      } else {
        this.animationFrameId = null;
      }
    };

    this.animationFrameId = requestAnimationFrame(animate);
  }

  private updateDisplay(number: number): void {
    try {
      if (this.outputText()) {
        const formattedText = this.formatNumber(number);
        this.setElementText(formattedText);
      }
      this.numberEmitter.emit(number);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('NumberAnimationDirective: Failed to update display', error);
      this.setElementText(number.toString());
      this.numberEmitter.emit(number);
    }
  }

  private setElementText(text: string): void {
    const element = this.el?.nativeElement;
    if (element) {
      element.textContent = text;
    }
  }

  private formatNumber(number: number): string {
    const formatter = this.formatter();

    if (typeof formatter === 'function') {
      return formatter(number);
    }

    if (formatter instanceof Intl.NumberFormat) {
      return formatter.format(number);
    }

    return this.createDefaultFormatter().format(number);
  }

  private createDefaultFormatter(): Intl.NumberFormat {
    const languageCode = this.getLanguageCode();
    return new Intl.NumberFormat(languageCode, { notation: 'compact' });
  }

  private getLanguageCode(): string {
    try {
      const context = this.securityManager.getIupicsUserContext();
      const language = context?.['#AD_Language'];
      return typeof language === 'string' && language.length >= 2 ? language.slice(0, 2) : 'en';
    } catch {
      return 'en';
    }
  }

  private parseNumber(value: number | string | null | undefined): number {
    if (typeof value === 'number') {
      return isNaN(value) ? 0 : value;
    }
    if (typeof value === 'string') {
      const parsed = Number(value);
      return isNaN(parsed) ? 0 : parsed;
    }
    return 0;
  }

  private easeOutCubic(t: number): number {
    return 1 - Math.pow(1 - t, 3);
  }

  private cancelCurrentAnimation(): void {
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }
}
